<template>
  <div>
    <b-overlay :show="isLoading" rounded="sm">
      <b-container fluid>
        <b-navbar toggleable="lg" type="dark" variant="light">
          <b-navbar-brand href="#" class="text-dark">
            <strong>Employees</strong>
          </b-navbar-brand>

          <b-navbar-toggle target="nav-collapse"></b-navbar-toggle>

          <b-collapse id="nav-collapse" is-nav>
            <!-- Right aligned nav items -->
            <b-navbar-nav class="ml-auto">
              <b-nav-form>
                <b-button
                  size="sm"
                  variant="outline-primary"
                  @click="showModal = !showModal"
                  class="mx-2"
                >
                  <font-awesome-icon icon="plus" />
                </b-button>

                <b-button size="sm" class="mr-3" variant="outline-info" @click="reloadTable">
                  <font-awesome-icon icon="sync" />
                </b-button>
                <b-form-group
                  label-cols-sm="3"
                  label-align-sm="right"
                  label-size="sm"
                  label-for="filterInput"
                >
                  <b-input-group size="sm">
                    <b-form-input
                      v-model="filter"
                      type="search"
                      id="filterInput"
                      placeholder="Type to Search"
                    ></b-form-input>
                    <b-input-group-append>
                      <b-button :disabled="!filter" @click="filter = ''">Clear</b-button>
                    </b-input-group-append>
                  </b-input-group>
                </b-form-group>
              </b-nav-form>
            </b-navbar-nav>
          </b-collapse>
        </b-navbar>

        <b-table
          class="mx-1"
          bordered
          sticky-header="calc(100vh - 200px)"
          ref="table"
          id="my-table"
          :busy.sync="isLoading"
          :items="getEmployeesTableData"
          :fields="tableFields"
          :filter="filter"
          :per-page="perPage"
          :current-page="currentPage"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          responsive="sm"
        >
          <template v-slot:cell(created_at)="data">{{ age(data.item.created_at) }}</template>
          <template v-slot:cell(actions)="data">
            <div class="text-nowrap">
              <b-button
                size="sm"
                class="mr-2"
                type="button"
                variant="outline-info"
                @click="onEdit(data.item)"
              >
                <font-awesome-icon v-b-tooltip.hover title="Edit Field" icon="edit" />
              </b-button>
              <b-button size="sm" type="button" variant="outline-danger">
                <font-awesome-icon v-b-tooltip.hover title="Delete Field" icon="trash" />
              </b-button>
            </div>
          </template>
        </b-table>
        <b-row>
          <b-col cols="2">
            <b-form-select size="sm" v-model="perPage" id="perPageSelect" :options="pageOptions"></b-form-select>
          </b-col>
          <b-col>
            <b-pagination
              class="float-right"
              v-model="currentPage"
              :total-rows="rows"
              :per-page="perPage"
              first-text="First"
              prev-text="Prev"
              next-text="Next"
              last-text="Last"
              aria-controls="my-table"
            ></b-pagination>
          </b-col>
        </b-row>
      </b-container>
    </b-overlay>

    <b-modal
      no-close-on-backdrop
      no-close-on-esc
      hide-header-close
      size="lg"
      :title="editEmployee ? 'Update Employee' : 'Create New Employee'"
      v-model="showModal"
    >
      <div>
        <div v-if="!submiting && !responseSuccess">
          <b-row>
            <b-col cols="6">
              <b-form-group>
                <label>
                  Job Title :
                  <span class="required-span" style="visibility: hidden;">*</span>
                </label>
                <b-form-input v-model="form.jobTitle" placeholder="Enter Job Title"></b-form-input>
              </b-form-group>
            </b-col>
            <b-col cols="6">
              <b-form-group>
                <label>
                  First Name :
                  <span class="required-span">*</span>
                </label>
                <b-form-input
                  v-model="$v.form.firstName.$model"
                  :state="$v.form.firstName.$dirty ? !$v.form.firstName.$error : null"
                  placeholder="Enter First Name"
                ></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="6">
              <b-form-group>
                <label>
                  Middle Name :
                  <span class="required-span" style="visibility: hidden;">*</span>
                </label>
                <b-form-input v-model="form.middleName" placeholder="Enter Middle Name"></b-form-input>
              </b-form-group>
            </b-col>
            <b-col cols="6">
              <b-form-group>
                <label>
                  Surname :
                  <span class="required-span">*</span>
                </label>
                <b-form-input
                  v-model="$v.form.surname.$model"
                  :state="$v.form.surname.$dirty ? !$v.form.surname.$error : null"
                  placeholder="Enter Surname"
                ></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="6">
              <b-form-group>
                <label>
                  Mobile :
                  <span class="required-span" style="visibility: hidden;">*</span>
                </label>
                <b-form-input type="number" v-model="form.mobile" placeholder="Enter Mobile"></b-form-input>
              </b-form-group>
            </b-col>
            <b-col cols="6">
              <b-form-group>
                <label>
                  Personal Email :
                  <span class="required-span" style="visibility: hidden;">*</span>
                </label>
                <b-form-input
                  type="email"
                  v-model="form.personalEmail"
                  placeholder="Enter Personal Email"
                ></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="6">
              <b-form-group>
                <label>
                  Company Email :
                  <span class="required-span">*</span>
                </label>
                <b-form-input
                  type="email"
                  v-model="$v.form.companyEmail.$model"
                  :state="$v.form.companyEmail.$dirty ? !$v.form.companyEmail.$error : null"
                  placeholder="Enter Company Email"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col cols="6">
              <b-form-group>
                <label>
                  Departments :
                  <span class="required-span" style="visibility: hidden;">*</span>
                </label>
                <v-select
                  :searchable="true"
                  :clearable="true"
                  label="label"
                  code="value"
                  :options="departmentsOptions"
                  v-model="form.departments"
                  aria-describedby
                  placeholder="Select Any Departments"
                ></v-select>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="6">
              <b-form-group>
                <label>
                  Manager :
                  <span class="required-span" style="visibility: hidden;">*</span>
                </label>
                <b-form-select v-model="form.Manager" placeholder="Select Manager"></b-form-select>
              </b-form-group>
            </b-col>
            <b-col cols="6">
              <b-form-group>
                <label>
                  Start Date :
                  <span class="required-span" style="visibility: hidden;">*</span>
                </label>
                <b-form-datepicker
                  v-model="form.startDate"
                  :hide-header="true"
                  placeholder="Select Start Date"
                  :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                  locale="en"
                  today-button
                  reset-button
                  close-button
                  class="mb-2"
                ></b-form-datepicker>
              </b-form-group>
            </b-col>
          </b-row>
        </div>
        <div v-else-if="submiting && !responseSuccess">
          <p>{{editEmployee ? 'Your Employee is being Updating......' : 'Your Employee is being Creating......'}}</p>
          <b-spinner type="grow" label="Loading..."></b-spinner>
        </div>
        <div v-else>
          <p class="my-2 text-center">
            {{ editEmployee ? 'Your Employee has been updated successfully.' : 'Your Employee has been added successfully.' }}
            <br />
          </p>
        </div>
      </div>
      <template v-slot:modal-footer>
        <b-button v-if="!submiting" variant="outline-danger" @click="resetModal">Close</b-button>
        <b-button
          v-if="!submiting && !responseSuccess"
          variant="outline-success"
          @click="checkValidation()"
        >{{ editEmployee ? 'Update' : 'Save' }}</b-button>
      </template>
    </b-modal>
  </div>
</template>
<script>
import { HTTP } from "../utils/HTTP";
// import { EventBus } from "@/event-bus.js";
import { validationMixin } from "vuelidate";
import { required } from "vuelidate/lib/validators";
import moment from "moment";
import "moment/locale/es";
export default {
  name: "EmployeePage",
  mixins: [validationMixin],
  data() {
    return {
      submiting: false,
      responseSuccess: false,
      editEmployee: false,
      showModal: false,
      tableFields: [
        { key: "created_at", label: "Age", sortable: true },
        { key: "job_title", label: "Job Title", sortable: true },
        { key: "name", label: "User Name", sortable: true },
        { key: "department", label: "Department", sortable: true },
        { key: "actions", label: "Actions", sortable: false }
      ],
      filter: null,
      perPage: 10,
      totalRows: 0,
      currentPage: 1,
      sortBy: "id",
      sortDesc: true,
      pageOptions: [5, 10, 25, 50, 75, 100],
      isLoading: false,
      form: {
        jobTitle: null,
        firstName: null,
        middleName: null,
        surname: null,
        mobile: null,
        personalEmail: null,
        companyEmail: null,
        departments: null,
        Manager: null,
        startDate: null
      },
      employeeId: null,
      departmentsOptions: [],
      userId: null
    };
  },
  validations: {
    form: {
      firstName: { required },
      surname: { required },
      companyEmail: { required }
    }
  },
  mounted() {
    this.getDepartmentsOptions();
  },
  computed: {
    rows() {
      return this.totalRows;
    }
  },
  methods: {
    reloadTable() {
      this.$root.$emit("bv::refresh::table", "my-table");
    },
    async getEmployeesTableData() {
      let formData = {
        filter: this.filter,
        sort: this.sortBy + "|" + (this.sortDesc == true ? "desc" : "asc"),
        per_page: this.perPage,
        page: this.currentPage
      };

      let token = "Bearer " + localStorage.getItem("accessToken");
      this.isLoading = true;
      let result = HTTP("post", "v1/get-employee-data", formData, {
        Authorization: token
      });

      return result
        .then(response => {
          const items = response.data.data;
          this.isLoading = false;
          this.totalRows = response.data.total;
          return items;
        })
        .catch(error => {
          this.isLoading = false;
          return [error];
        });
    },
    age(value) {
      moment.locale("en");
      return moment.utc(value).fromNow();
    },
    onEdit(value) {
      this.employeeId = value.id;
      this.userId = value.member_id;
      this.editEmployee = true;
      this.showModal = true;
      this.form = {
        jobTitle: value.job_title,
        firstName: value.firstname,
        middleName: null,
        surname: value.lastname,
        mobile: value.phone,
        personalEmail: null,
        companyEmail: value.email,
        departments: { label: value.department, value: value.department_id },
        Manager: null,
        startDate: value.start_date
      };
    },
    checkValidation() {
      this.$v.form.$touch();
      if (this.$v.form.$anyError) {
        return;
      } else if (this.editEmployee) {
        this.updateEmployee().then(this.reloadTable);
      } else {
        this.saveEmployee().then(this.reloadTable);
      }
    },
    async saveEmployee() {
      let formData = {
        job_title: this.form.jobTitle,
        first_name: this.form.firstName,
        middle_name: this.form.middleName,
        surname: this.form.surname,
        mobile: this.form.mobile,
        personal_email: this.form.personalEmail,
        company_email: this.form.companyEmail,
        departments: this.form.departments.value,
        manager_id: this.form.Manager,
        start_date: this.form.startDate
      };
      try {
        let token = "Bearer " + localStorage.getItem("accessToken");
        this.submiting = true;
        this.isLoading = true;
        await HTTP("post", "v1/create-employee", formData, {
          Authorization: token
        });
        this.submiting = false;
        this.isLoading = false;
        this.responseSuccess = true;
      } catch (err) {
        return [err];
      }
    },
    async updateEmployee() {
      let formData = {
        employee_id: this.employeeId,
        job_title: this.form.jobTitle,
        first_name: this.form.firstName,
        middle_name: this.form.middleName,
        surname: this.form.surname,
        mobile: this.form.mobile,
        personal_email: this.form.personalEmail,
        company_email: this.form.companyEmail,
        departments: this.form.departments.value,
        Manager: this.form.Manager,
        start_date: this.form.startDate,
        user_id: this.userId
      };
      try {
        let token = "Bearer " + localStorage.getItem("accessToken");
        this.submiting = true;
        this.isLoading = true;
        await HTTP("post", "v1/update-employee", formData, {
          Authorization: token
        });
        this.submiting = false;
        this.isLoading = false;
        this.responseSuccess = true;
      } catch (err) {
        return [err];
      }
    },
    async getDepartmentsOptions() {
      try {
        let token = "Bearer " + localStorage.getItem("accessToken");
        let result = await HTTP("get", "v1/get-departments-options", null, {
          Authorization: token
        });
        this.departmentsOptions = result.data.departments;
      } catch (error) {
        this.isLoading = false;
      }
    },
    resetForm() {
      this.form = {
        jobTitle: null,
        firstName: null,
        middleName: null,
        surname: null,
        mobile: null,
        personalEmail: null,
        companyEmail: null,
        departments: null,
        Manager: null,
        startDate: null
      };
    },
    resetModal() {
      this.resetForm();
      this.submiting = false;
      this.responseSuccess = false;
      this.editEmployee = false;
      this.showModal = false;
      this.$v.$reset();
    }
  }
};
</script>